/**
 * @file This file hosts the entry point for generating the schema of a v2 List Report Page.
 * The schema is yet incomplete and is normally ignored by the runtime.
 * For manual tests it can be made active by switching useGenericSchemaHandling in pageAccess.ts to true.
 */

import type { AnnotationTerm } from '@sap-ux/vocabularies-types';
import type { LineItem } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import type { EntityTypeAnnotations_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import { ControlType, LogSeverity, SchemaTag, UIVOCABULARYDOT } from '@sap/ux-specification-types';
import type {
    GenerateAppSchemaParameters,
    ProcessingRule,
    SapUiGenericAppPageSettings,
    SchemaHandlingParams
} from '@sap/ux-specification-types';
import type { ListReportNewConfigV2 } from '@sap/ux-specification-types/src/v2/genericSchemaHandling/pages/ListReportNewConfigV2';
import { pageLayoutInformation, syncRule } from '../../../common/decoration/decorators';
import { FE_TEMPLATE_V2_LIST_REPORT, SAPUI5_VIEW_EXTENSION_LIST_REPORT } from '@sap/ux-specification-types/src/v2';
import { TableTypeV2Enum } from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Table';
import { log } from '../../../../extensionLogger';
import i18next from 'i18next';
import { getManifestPathToPageSettings, getManifestPropertyByPath, isEmpty } from '../../utils';
import { ListHeader } from '../controls/ListHeader';
import { ListReportFilterBar } from '../controls/FilterBar';
import { getDefaultTableType, Table } from '../controls/Table';
import type { TableParams } from '../controls/Table';
import type { Toolbar } from '../controls/Action';
import { adaptProcessingRuleForToolbar } from '../controls/Action';
import { hasPropertyTypeError } from '../../generate/manifestPropertyUtils';
import {
    DataFieldTarget,
    getLineItemAnnotation,
    getLineItemInfo,
    getUIAnnotationForEntitySet
} from '../../generate/utils';
import type { LineItemInfo } from '../../generate/utils';

/**
 * An instance of this type collects top-level information about a List Report page.
 * It will be created by the processingRuleAdapter of ListReport and passed as specificParams to
 * the processingRuleAdapters of the child controls.
 *
 * @property settings - the component settings from the manifest
 * @property entityType - Metadata of the entity type of the (main) entity set of this LR page
 * @property uIAnnotations - The ui annotations valid for this page
 * @property tableType - The type of the table used on this LR page
 * @property lineItemInfo - Information about the line item configured for this LR page (if any)
 */
export type LRSchemaHandlingParams = {
    settings?: SapUiGenericAppPageSettings;
    entityType: Edm.EntityType;
    uIAnnotations: EntityTypeAnnotations_UI;
    tableType: TableTypeV2Enum;
    lineItemInfo?: LineItemInfo;
};

// ListReportNew
@pageLayoutInformation({
    id: SAPUI5_VIEW_EXTENSION_LIST_REPORT,
    pageId: FE_TEMPLATE_V2_LIST_REPORT
})
@syncRule({
    processingRuleAdapter(
        processingRule: ProcessingRule,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): void {
        const { entityType, uIAnnotations } = getUIAnnotationForEntitySet(generateParameters); // would log an error in case these entities are missing
        const manifestPathToPageSettings = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
        const { parent: pageSettings } = getManifestPropertyByPath(
            generateParameters.manifest,
            manifestPathToPageSettings
        );
        const settings = pageSettings as SapUiGenericAppPageSettings;
        const tableSettings = settings?.tableSettings;
        const tableType = tableSettings?.['type'] || settings?.tableType || getDefaultTableType(entityType);
        const lineItemAnnotation = getLineItemAnnotation(uIAnnotations, settings, generateParameters.logger);
        let lineItemInfo: LineItemInfo;
        if (lineItemAnnotation) {
            const lineItemName =
                lineItemAnnotation.term + (lineItemAnnotation.qualifier ? `#${lineItemAnnotation.qualifier}` : '');
            const accessor = lineItemName.replace(UIVOCABULARYDOT, '');
            const lineItemDefinition = uIAnnotations[accessor] as AnnotationTerm<LineItem>;
            lineItemInfo = getLineItemInfo(
                lineItemName,
                lineItemDefinition,
                entityType,
                tableType,
                generateParameters,
                false
            );
        }
        const specificParams: LRSchemaHandlingParams = {
            settings,
            entityType,
            uIAnnotations,
            tableType,
            lineItemInfo
        };
        // Further processing will be done on level of properties of this class
        processingRule.referenceAdaptation = {
            specificParams
        };
    }
})
export class ListReportNew implements ListReportNewConfigV2 {
    // fitContent
    @syncRule({
        flex: {
            controlType: (): ControlType => ControlType.DynamicPage
        },
        processingRuleAdapter(processingRule: ProcessingRule): void {
            processingRule.controlId = 'page';
        }
    })
    fitContent?: boolean;

    // header
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule): void {
            processingRule.element[SchemaTag.propertyIndex] = 0;
            processingRule.referenceAdaptation = {
                syncRuleProvider: ListHeader
            };
        }
    })
    header?: ListHeader;

    // filterBar
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const lRSchemaHandlingParams = schemaHandlingParams.specificParams as LRSchemaHandlingParams;
            if (lRSchemaHandlingParams.settings?.isWorklist) {
                // no filterbar shown in worklist apps
                delete processingRule.element;
                return;
            }
            processingRule.element[SchemaTag.propertyIndex] = 1;
            processingRule.referenceAdaptation = {
                syncRuleProvider: ListReportFilterBar
            };
        }
    })
    filterBar?: ListReportFilterBar;

    // table
    @syncRule({
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            const lRSchemaHandlingParams = schemaHandlingParams.specificParams as LRSchemaHandlingParams;
            processingRule.element[SchemaTag.icon] = 'Table'; // do not use member of enum UiIcons in @sap-ux/ui-components to avoid unwanted dependency
            processingRule.element[SchemaTag.propertyIndex] = 2;
            const lrSettings = lRSchemaHandlingParams.settings;
            const tableType = lRSchemaHandlingParams.tableType;

            const tableParams: TableParams = {
                settings: lrSettings,
                tableType,
                tableId: 'listReport',
                lineItemInfo: lRSchemaHandlingParams.lineItemInfo,
                entityType: lRSchemaHandlingParams.entityType
            };

            // Check whether we are in a multi-view multi-table scenario.
            const quickVariantSelectionX =
                !hasPropertyTypeError(lrSettings, 'quickVariantSelectionX', generateParameters.logger, 'object') &&
                lrSettings?.quickVariantSelectionX;

            let suffix: string;
            if (quickVariantSelectionX) {
                if (isEmpty(quickVariantSelectionX['variants'])) {
                    log(generateParameters.logger, {
                        severity: LogSeverity.Warning,
                        message: i18next.t('QUICKVARIANTSELECTIONXNOVARIANTS')
                    });
                } else {
                    suffix = 'Top';
                    if (lrSettings.quickVariantSelection) {
                        // Do not use quickVariantSelection and quickVariantSelectionX together
                        log(generateParameters.logger, {
                            severity: LogSeverity.Error,
                            message: i18next.t('QUICKVARIANTSELECTIONDUPLICATE')
                        });
                    }
                    // Preliminary setting of multiTableInfo. Will be enhanced later.
                    tableParams.multiTableInfo = {
                        usedTableTypes: {
                            nonResponsive: tableType === TableTypeV2Enum.ResponsiveTable ? 0 : 1,
                            responsive: tableType === TableTypeV2Enum.ResponsiveTable ? 1 : 0
                        }
                    };
                }
            }

            processingRule.referenceAdaptation = {
                suffix,
                specificParams: tableParams,
                syncRuleProvider: Table
            };
        }
    })
    table?: Table;

    // footer
    @syncRule({
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const lRSchemaHandlingParams = schemaHandlingParams.specificParams as LRSchemaHandlingParams;
            processingRule.element[SchemaTag.propertyIndex] = 3;
            adaptProcessingRuleForToolbar(
                processingRule,
                DataFieldTarget.Footer,
                lRSchemaHandlingParams.lineItemInfo?.dataFieldInfos
            );
        }
    })
    footer?: Toolbar;
}
